import config from '../../config';
import Utils from '../../Utils';
import { SAMAClient } from '@sama-communications/sdk';
import ProxyService from '../../Proxy';
import AbstractChatService from '../AbstractChatService';
import BlockList from './BlockList';
import type { IChatRealTimeService } from '../../types/chat';
import type { Calls, Chat, PlainObject } from '../../types';
import { CallSignalingModuleIdentifier, CallType } from '../../types/enums';

export default class SAMAChatService extends AbstractChatService implements IChatRealTimeService {
  public samaClient: SAMAClient;
  public blockList: BlockList;
  public currentUserNativeId: Chat.UserId = 0;

  constructor(proxy: ProxyService) {
    super(proxy);

    this.samaClient = new SAMAClient({
      endpoint: {
        ws: config.chatProtocol.websocket || '',
        http: `https://${config.endpoints.api}`,
      },
      organization_id: `${config.creds.appId}`,
      disableAutoReconnect: true,
    });

    this.blockList = new BlockList(this.samaClient);
  }

  public get connectionStatus(): string {
    return '';
  }

  public get currentUserId(): Chat.UserId {
    return this.currentUserNativeId || 0;
  }

  public async connect(params: Chat.ConnectionParams): Promise<boolean> {
    Utils.DLog('[Chat]', 'Connect with parameter:', params);

    this.samaClient.deviceId = params.deviceId ?? Utils.getBsonObjectId();

    this.removeAllSamaListeners();
    this.addAllSamaListeners();

    if (!this.isConnected) {
      await this.samaClient.connect();

      this.isConnected = true;
    }

    const authorizedUser = await this.samaClient.socketLogin({
      user: { userId: params.userId, password: params.password },
    });
    this.currentUserNativeId = authorizedUser['native_id'];

    this.isLogout = false;

    return true;
  }

  public async disconnect(): Promise<boolean> {
    Utils.DLog('[Chat]', 'disconnect');

    this.isConnected = false;

    try {
      await this.samaClient.disconnectSocket();
      await this.samaClient.disconnect();
      this.currentUserNativeId = 0;
      return true;
    } catch {
      return false;
    }
  }

  public terminate(): void {
    Utils.DLog('[Chat]', 'terminated');

    this.isConnected = false;
    this.currentUserNativeId = 0;

    this.samaClient.disconnect();
  }

  private onSamaConnectedListener(): void {
    Utils.DLog('[Chat]', this.samaClient.deviceId, '[connected]');

    this.isConnected = true;

    Utils.safeCallbackCall(this.onChatStatusListener)('connect');
  }

  private onSamaDisconnectListener(): void {
    Utils.DLog('[Chat]', this.samaClient.deviceId, '[disconnect]');

    this.isConnected = false;
    this.isLogout = true;

    Utils.safeCallbackCall(this.onChatStatusListener)('disconnect');
    Utils.safeCallbackCall(this.onDisconnectedListener)();
  }

  private onSamaMessageEventListener(message: any): void {
    Utils.DLog('[Chat]', '[packet]', this.samaClient.deviceId, message);
  }

  private onSamaMessageListener(message: any): void {
    Utils.DLog('[Chat]', '[onMessage]', this.samaClient.deviceId, message);

    const { _id, cid, c_type, from, body, t, x, attachments, created_at } = message;

    const messageParams: Chat.Message = {
      id: _id,
      dialog_id: cid,
      body: body,
      extension: Object.assign({ date_sent: t, attachments }, x),
    };

    Utils.safeCallbackCall(this.onMessageListener)(from, messageParams);
  }

  private onSamaSystemMessageListener(message: any): void {
    Utils.DLog('[Chat]', '[onSystem]', this.samaClient.deviceId, message);

    const { _id, from, t, x } = message;

    const systemMessage: Chat.SystemMessage = {
      id: _id,
      body: null as unknown as string,
      userId: from,
      extension: Object.assign({ date_sent: t }, x),
    };

    if (systemMessage.extension?.['moduleIdentifier'] === CallSignalingModuleIdentifier) {
      return this.onWebRTCSignalingSystemMessage(systemMessage);
    }

    Utils.safeCallbackCall(this.onSystemMessageListener)(systemMessage);
  }

  private onWebRTCSignalingSystemMessage(systemMessage: Chat.SystemMessage<Calls.ExtensionParams>): void {
    const extension = systemMessage.extension ?? {};

    Object.keys(extension).forEach((key) => {
      switch (key) {
        case 'callerID':
          extension[key] = parseInt(String(extension[key] || '0'));
          break;

        case 'callType':
          const type = extension[key];
          extension[key] = String(type) === '1' ? CallType.VIDEO : String(type) === '2' ? CallType.AUDIO : undefined;
          break;

        case 'opponentsIDs':
          extension[key]?.map((opponentID) => Number(opponentID || '0'));
          break;

        case 'iceCandidates':
          (extension[key] as unknown[] | undefined)?.forEach((iceCandidate: unknown) => {
            const c = iceCandidate as PlainObject;
            Object.keys(c).forEach((iceObjectKey) => {
              if (iceObjectKey === 'sdpMLineIndex') {
                c[iceObjectKey] = parseInt(String(c[iceObjectKey] || '0'));
              }
            });
          });
          break;

        default:
          break;
      }
    });

    return Utils.safeCallbackCall(this.webrtcSignalingProcessor.onMessage)(
      systemMessage.userId,
      systemMessage.extension
    );
  }

  private onSamaMessageStatusListener(message: any): void {
    Utils.DLog('[Chat]', '[onStatus]', this.samaClient.deviceId, message);

    const { ids, cid, from } = message;

    for (const messageId of ids) {
      Utils.safeCallbackCall(this.onReadStatusListener)(messageId, cid, from);
    }
  }

  private onSamaUserLastActivityListener(message: any): void {
    Utils.DLog('[Chat]', '[onLastActivity]', this.samaClient.deviceId, message);

    Object.entries(message).forEach(([userId, lastActivityTs]) => {
      Utils.safeCallbackCall(this.onLastUserActivityListener)(Number(userId), Number(lastActivityTs));
    });
  }

  private onSamaTypingListener(message: any): void {
    Utils.DLog('[Chat]', '[onTyping]', this.samaClient.deviceId, message);

    const { cid, from, status } = message;
    const isTyping = status === 'start';

    Utils.safeCallbackCall(this.onMessageTypingListener)(isTyping, from, cid);
  }

  private onSamaMessageEditListener(message: any): void {
    Utils.DLog('[Chat]', '[onEdit]', this.samaClient.deviceId, message);

    const { id, cid, from, body } = message;

    const messageEditParams = {
      messageId: id,
      isLast: false,
      updatedBody: body,
      dialogId: cid,
      userId: from,
      extraParams: {},
    };

    Utils.safeCallbackCall(this.onMessageUpdateListener)(
      messageEditParams.messageId,
      messageEditParams.isLast,
      messageEditParams.updatedBody,
      messageEditParams.dialogId,
      messageEditParams.userId,
      messageEditParams.extraParams
    );
  }

  private onSamaDeleteListener(message: any): void {
    Utils.DLog('[Chat]', '[onDelete]', this.samaClient.deviceId, message);

    const { ids, cid, from } = message;

    for (const messageId of ids) {
      Utils.safeCallbackCall(this.onMessageDeleteListener)(messageId, cid, from);
    }
  }

  private onSamaMessageReactionListener(messageReaction: any): void {
    Utils.DLog('[Chat]', '[onReaction]', this.samaClient.deviceId, messageReaction);

    const { mid, cid, from, add, remove } = messageReaction;

    Utils.safeCallbackCall(this.onMessageReactionsListener)(mid, from, cid, add, remove);
  }

  public async ping(): Promise<void> {
    const pong = await this.samaClient.ping();
  }

  public async sendIsTypingStatus(dialogId: Chat.DialogId): Promise<void> {
    const requestParams = {
      cid: dialogId,
      status: 'start',
    };

    await this.samaClient.sendTypingStatus(requestParams);
  }

  public async sendIsStopTypingStatus(dialogId: Chat.DialogId): Promise<void> {
    const requestParams = {
      cid: dialogId,
      status: 'stop',
    };

    await this.samaClient.sendTypingStatus(requestParams);
  }

  public async send(dialogId: Chat.DialogId, message: Chat.MessageParams): Promise<string> {
    const mid = message.id ?? Utils.getBsonObjectId();
    const x = message.extension ?? {};
    const attachments = x?.['attachments'];
    delete x?.['attachments'];

    const requestParams = {
      mid,
      body: message.body,
      x,
      attachments,
      cid: dialogId,
    };

    const ack = await this.samaClient.messageCreate(requestParams);

    return ack?.server_mid ?? mid;
  }

  public sendSystemMessage(userId: Chat.UserId, message: Chat.SystemMessageParams): Promise<string>;
  public sendSystemMessage(userIds: Array<Chat.UserId>, message: Chat.SystemMessageParams): Promise<string>;
  public sendSystemMessage(dialogId: Chat.DialogId, message: Chat.SystemMessageParams): Promise<string>;
  public async sendSystemMessage(
    userIdsOrDialogId: Chat.UserId | Array<Chat.UserId> | Chat.DialogId,
    message: Chat.SystemMessageParams
  ): Promise<string> {
    const mid = message.id ?? Utils.getBsonObjectId();

    const destinationParams = Array.isArray(userIdsOrDialogId)
      ? { uids: userIdsOrDialogId }
      : typeof userIdsOrDialogId === 'number'
        ? { uids: [userIdsOrDialogId] }
        : { cid: userIdsOrDialogId };

    const requestParams = {
      mid,
      x: message.extension ?? {},
      ...destinationParams,
    };

    this.samaClient.messageSystem(requestParams);

    return mid;
  }

  public async sendWebRTCSignalingMessage(userId: number, message: Chat.SystemMessageParams): Promise<string> {
    return await this.sendSystemMessage(userId, message);
  }

  public async sendDeliveredStatus(params: Chat.MessageStatusParams): Promise<void> {}

  public async sendReadStatus(params: Chat.MessageStatusParams): Promise<void> {
    const requestParams = {
      cid: params.dialogId,
      mids: [params.messageId],
    };

    await this.samaClient.markConversationAsRead(requestParams);
  }

  public async markDialogAsRead(params: Chat.DialogReadParams): Promise<void> {
    const requestParams = {
      cid: params.dialogId,
    };

    await this.samaClient.markConversationAsRead(requestParams);
  }

  public async updateReaction(messageId: string, addReaction: string, removeReaction: string): Promise<void> {
    await this.samaClient.reactionsUpdate(messageId, addReaction, removeReaction);
  }

  public async editMessage(params: Chat.EditMessageParams): Promise<void> {
    const requestParams = {
      mid: params.originMessageId,
      body: params.body,
    };

    await this.samaClient.messageEdit(requestParams);
  }

  public async deleteMessage(params: Chat.DeleteMessageParams): Promise<void> {
    const requestParams = {
      cid: params.dialogId,
      mids: [params.messageId],
      type: 'all',
    };

    await this.samaClient.messageDelete(requestParams);
  }

  public async getLastUserActivity(userId: Chat.UserId): Promise<Chat.LastActivity> {
    const result = await this.samaClient.getUserActivity([userId]);

    if (!(userId in result)) {
      throw new Error('User not found');
    }

    const activityDate = result[userId];

    if (activityDate === 0) {
      return { userId, seconds: activityDate };
    }

    return { userId, seconds: !activityDate ? activityDate : Math.ceil(+new Date() / 1000) - activityDate };
  }

  public async subscribeToUserLastActivityStatus(userId: Chat.UserId): Promise<void> {
    return await this.samaClient.subscribeToUserActivity(userId);
  }

  public async unsubscribeFromUserLastActivityStatus(userId: Chat.UserId): Promise<void> {
    return await this.samaClient.unsubscribeFromUserActivity();
  }

  public async markActive(): Promise<void> {
    return await this.samaClient.setActivityStatus(false);
  }

  public async markInactive(): Promise<void> {
    return await this.samaClient.setActivityStatus(true);
  }

  private addAllSamaListeners() {
    this.samaClient.onConnectEvent = this.onSamaConnectedListener.bind(this);
    this.samaClient.onDisconnectEvent = this.onSamaDisconnectListener.bind(this);

    this.samaClient.onMessageListener = this.onSamaMessageEventListener.bind(this);
    this.samaClient.onMessageEvent = this.onSamaMessageListener.bind(this);
    this.samaClient.onSystemMessageEvent = this.onSamaSystemMessageListener.bind(this);

    this.samaClient.onMessageStatusListener = this.onSamaMessageStatusListener.bind(this);
    this.samaClient.onMessageReactionsListener = this.onSamaMessageReactionListener.bind(this);

    this.samaClient.onMessageEditListener = this.onSamaMessageEditListener.bind(this);
    this.samaClient.onMessageDeleteListener = this.onSamaDeleteListener.bind(this);

    this.samaClient.onUserActivityListener = this.onSamaUserLastActivityListener.bind(this);
    this.samaClient.onUserTypingListener = this.onSamaTypingListener.bind(this);
  }

  private removeAllSamaListeners() {
    this.samaClient.onConnectEvent = void 0;
    this.samaClient.onDisconnectEvent = void 0;

    this.samaClient.onMessageListener = void 0;
    this.samaClient.onMessageEvent = void 0;
    this.samaClient.onSystemMessageEvent = void 0;

    this.samaClient.onMessageStatusListener = void 0;
    this.samaClient.onMessageReactionsListener = void 0;

    this.samaClient.onMessageEditListener = void 0;
    this.samaClient.onMessageDeleteListener = void 0;

    this.samaClient.onUserActivityListener = void 0;
    this.samaClient.onUserTypingListener = void 0;
  }
}
